import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Save, 
  Plus, 
  Trash2, 
  Upload, 
  Clock, 
  Eye, 
  X, 
  Edit3,
  Image as ImageIcon
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...extra
  };
};

const labelMap = {
  kicker: 'Events Section Tag / Kicker',
  title: 'Events Main Heading',
  description: 'Events Overview Description'
};

export default function StudentLifeEventsForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({
    kicker: '',
    title: '',
    description: '',
    events: []
  });

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    description: false
  });

  const [pendingEvents, setPendingEvents] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [editingEventIndex, setEditingEventIndex] = useState(null);
  const [isAddingEvent, setIsAddingEvent] = useState(false);
  const [modalEventData, setModalEventData] = useState(null);
  const [imageUploading, setImageUploading] = useState({});
  const [imageProgress, setImageProgress] = useState({});
  const [mediaMap, setMediaMap] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);
  const [highlightsText, setHighlightsText] = useState('');
  const [scheduleText, setScheduleText] = useState('');

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([loadMediaMap(), loadSection()]);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(
        `${apiBase}/studentlife/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: getAuthHeaders({ 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' })
        }
      );
      
      if (!response.ok) return;
      
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path || media.filepath || '';
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
            const numId = Number(mediaId);
            if (!isNaN(numId)) map[numId] = mediaUrl;
          }
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[StudentLifeEventsForm] Media fetch error:', err.message);
    }
  };

  const getMediaUrl = (mediaId) => {
    if (!mediaId) return null;
    const str = String(mediaId).trim();
    if (!str) return null;

    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('data:')) {
      return str;
    }

    if (str.startsWith('/')) {
      const origin = (apiBase || '').replace(/\/api\/?$/, '');
      return origin ? `${origin}${str}` : str;
    }

    if (mediaMap[str]) return mediaMap[str];
    if (mediaMap[mediaId]) return mediaMap[mediaId];

    const resolved = resolveMediaSrc(mediaId, mediaMap);
    if (resolved) {
      if (resolved.startsWith('/')) {
        const origin = (apiBase || '').replace(/\/api\/?$/, '');
        return origin ? `${origin}${resolved}` : resolved;
      }
      return resolved;
    }

    return str;
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      let eventsSection = null;

      const adminResponse = await fetch(
        `${apiBase}/studentlife/cms/sections`,
        { 
          credentials: 'include',
          headers: getAuthHeaders()
        }
      );
      
      if (adminResponse.ok) {
        const result = await adminResponse.json();
        if (result.ok && Array.isArray(result.data)) {
          eventsSection = result.data.find(s => s.section_type === 'student-life-events');
        }
      }

      if (!eventsSection) {
        const publicResponse = await fetch(
          `${apiBase}/studentlife/cms/sections?published=true`,
          { 
            credentials: 'include',
            headers: getAuthHeaders()
          }
        );
        if (publicResponse.ok) {
          const result = await publicResponse.json();
          if (result.ok && Array.isArray(result.data)) {
            eventsSection = result.data.find(s => s.section_type === 'student-life-events');
          }
        }
      }

      if (!eventsSection) {
        throw new Error('Events section not found');
      }

      setSectionId(eventsSection.id);

      const content = eventsSection.content_published || eventsSection.content || {};
      const parsedContent = typeof content === 'string' ? JSON.parse(content) : content;
      const enData = parsedContent.en || parsedContent;

      let normalizedEvents = [];
      if (Array.isArray(enData.events)) {
        normalizedEvents = enData.events.map((e, idx) => ({
          title: e.title || e.name || `Event ${idx + 1}`,
          category: e.category || '',
          subtitle: e.subtitle || e.date || '',
          date: e.date || e.subtitle || '',
          description: e.description || '',
          highlights: Array.isArray(e.highlights) ? e.highlights : [],
          schedule: Array.isArray(e.schedule) ? e.schedule : [],
          images: Array.isArray(e.images) ? e.images : (e.image ? [e.image] : [])
        }));
      }

      const initialData = {
        kicker: enData.kicker || 'Campus Events & Calendar',
        title: enData.title || 'Major Campus Events & Competitions',
        description: enData.description || 'From athletic tournaments to business pitches and STEM discovery, our signature events bring the whole Permata Harapan family together.',
        events: normalizedEvents
      };

      const pendingFieldsMap = { kicker: false, title: false, description: false };
      const pendingEventsMap = {};

      const pendingRes = await fetch(`${apiBase}/cms/section/${eventsSection.id}/with-pending`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      if (pendingRes.ok) {
        const pendingData = await pendingRes.json();
        if (pendingData.success && pendingData.data) {
          const pendingChanges = pendingData.data.pending_changes || [];
          
          if (Array.isArray(pendingChanges)) {
            pendingChanges.forEach((change) => {
              if (change.status === 'pending' && change.locale === 'en') {
                let val = change.new_value;
                if (typeof val === 'string' && (val.startsWith('{') || val.startsWith('['))) {
                  try { val = JSON.parse(val); } catch (e) {}
                }

                if (change.field_id === 'kicker' || change.field_id === 'title' || change.field_id === 'description') {
                  pendingFieldsMap[change.field_id] = true;
                  if (val !== undefined && val !== null) {
                    initialData[change.field_id] = val;
                  }
                } else if (change.field_id && change.field_id.includes('events.')) {
                  const match = change.field_id.match(/events\.(\d+)\.(add|delete|update)/);
                  if (match) {
                    const eventIdx = parseInt(match[1], 10);
                    const op = match[2];
                    if (op === 'delete') {
                      pendingEventsMap[`event_${eventIdx}_delete`] = true;
                    } else if (op === 'add') {
                      pendingEventsMap[`event_${eventIdx}_add`] = true;
                      if (val && !initialData.events[eventIdx]) {
                        initialData.events[eventIdx] = val;
                      }
                    } else {
                      pendingEventsMap[`event_${eventIdx}`] = true;
                      if (val && initialData.events[eventIdx]) {
                        initialData.events[eventIdx] = val;
                      }
                    }
                  }
                }
              }
            });
          }
        }
      }

      setFormData(initialData);
      setOriginalData(initialData);
      setPendingFields(pendingFieldsMap);
      setPendingEvents(pendingEventsMap);
    } catch (err) {
      console.error('[StudentLifeEventsForm] Load error:', err);
      toast.error('Error loading events section: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleAddEvent = () => {
    const newEvent = {
      title: '',
      category: '',
      subtitle: '',
      date: '',
      description: '',
      highlights: [],
      schedule: [],
      images: []
    };
    setIsAddingEvent(true);
    setEditingEventIndex(formData.events.length);
    setHighlightsText('');
    setScheduleText('');
    setModalEventData(newEvent);
  };

  const handleOpenEventModal = (index) => {
    setIsAddingEvent(false);
    setEditingEventIndex(index);
    const target = formData.events[index] || {};
    const hl = Array.isArray(target.highlights) 
      ? target.highlights.join('\n') 
      : (target.highlights || '');
    const sc = Array.isArray(target.schedule)
      ? target.schedule.map(s => {
          if (typeof s === 'string') return s;
          if (typeof s === 'object' && s !== null) {
            const b = s.phase || s.time || s.badge || s.step || s.title || '';
            const t = s.text || s.activity || s.description || s.detail || '';
            return b ? `${b}: ${t}` : t;
          }
          return String(s);
        }).join('\n')
      : (target.schedule || '');
    setHighlightsText(hl);
    setScheduleText(sc);
    setModalEventData({
      ...target,
      title: target.title || '',
      category: target.category || '',
      subtitle: target.subtitle || target.date || '',
      date: target.date || target.subtitle || '',
      description: target.description || '',
      highlights: Array.isArray(target.highlights) ? [...target.highlights] : [],
      schedule: Array.isArray(target.schedule) ? [...target.schedule] : [],
      images: Array.isArray(target.images) ? [...target.images] : (target.image ? [target.image] : [])
    });
  };

  const handleCloseEventModal = () => {
    setEditingEventIndex(null);
    setModalEventData(null);
    setHighlightsText('');
    setScheduleText('');
    setIsAddingEvent(false);
  };

  const handleDeleteEvent = async (index) => {
    if (pendingEvents[`event_${index}`] || pendingEvents[`event_${index}_delete`] || pendingEvents[`event_${index}_add`]) {
      toast.info('This event has a pending approval');
      return;
    }

    const eventToDelete = formData.events[index];
    if (!window.confirm(`Delete event "${eventToDelete.title || `Event ${index + 1}`}"? This request will be submitted to Principal for approval.`)) return;

    const eventKey = `event_${index}`;
    setSubmitting(prev => ({ ...prev, [eventKey]: true }));

    try {
      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `events.${index}.delete`,
          field_name: `Delete Event: ${eventToDelete.title || `Event ${index + 1}`}`,
          old_value: eventToDelete || {},
          new_value: null,
          locale: 'en'
        })
      });

      const result = await response.json();
      if (result.success || response.ok) {
        toast.success(`Deletion request for "${eventToDelete.title || `Event ${index + 1}`}" submitted for Principal approval`);
        setPendingEvents(prev => ({
          ...prev,
          [`event_${index}_delete`]: true
        }));
      } else {
        toast.error(result.error || result.message || 'Failed to submit event deletion');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [eventKey]: false }));
    }
  };

  const handleImageUpload = (eventIndex, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Please upload an image file (JPEG, PNG, WebP)');
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setImageUploading(prev => ({ ...prev, [eventIndex]: true }));
    setImageProgress(prev => ({ ...prev, [eventIndex]: 0 }));

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    uploadFormData.append('section_type', 'student-life-events');

    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const percent = Math.round((event.loaded / event.total) * 100);
        setImageProgress(prev => ({ ...prev, [eventIndex]: percent }));
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status === 200 || xhr.status === 201) {
        try {
          const response = JSON.parse(xhr.responseText);
          if (response.success || response.ok) {
            const origin = (apiBase || '').replace(/\/api\/?$/, '');
            const returnedUrl = response.url || response.file_path || (response.filename ? `/api/uploads/studentlife/${response.filename}` : '');
            const fullUrl = returnedUrl.startsWith('http') ? returnedUrl : (returnedUrl ? `${origin}${returnedUrl}` : '');
            const finalUrl = fullUrl || returnedUrl || String(response.media_id || response.id || '');

            setModalEventData(prev => {
              const currentImgs = Array.isArray(prev.images) ? [...prev.images] : [];
              return {
                ...prev,
                images: [...currentImgs, finalUrl]
              };
            });

            const mediaId = response.media_id || response.id;
            if (mediaId) {
              setMediaMap(prev => ({
                ...prev,
                [mediaId]: finalUrl,
                [String(mediaId)]: finalUrl
              }));
            }

            toast.success('Event photo uploaded successfully');
          } else {
            toast.error(response.error || response.message || 'Upload failed');
          }
        } catch (pe) {
          toast.error('Invalid server response');
        }
      } else {
        toast.error(`Upload failed (HTTP ${xhr.status})`);
      }
      setImageUploading(prev => ({ ...prev, [eventIndex]: false }));
    });

    xhr.addEventListener('error', () => {
      toast.error('Network error during upload');
      setImageUploading(prev => ({ ...prev, [eventIndex]: false }));
    });

    xhr.open('POST', `${apiBase}/studentlife/cms/media/upload`);
    xhr.withCredentials = true;
    const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    xhr.send(uploadFormData);
  };

  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldName,
            field_name: labelMap[fieldName] || fieldName,
            new_value: formData[fieldName],
            old_value: originalData[fieldName],
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`Submitted for approval`);
        setPendingFields(prev => ({ ...prev, [fieldName]: true }));
        setOriginalData(prev => ({ ...prev, [fieldName]: formData[fieldName] }));
      } else {
        if (response.status === 409) {
          toast.error('This field already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleSubmitEvent = async (eventIndex) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    if (!modalEventData || !modalEventData.title?.trim()) {
      toast.warning('Please enter an event title');
      return;
    }

    const cleanHighlights = (highlightsText || '')
      .split('\n')
      .map(h => h.trim())
      .filter(Boolean);

    const cleanSchedule = (scheduleText || '')
      .split('\n')
      .map(s => s.trim())
      .filter(Boolean);

    const ev = {
      ...modalEventData,
      title: (modalEventData.title || '').trim(),
      category: (modalEventData.category || '').trim(),
      subtitle: (modalEventData.subtitle || modalEventData.date || '').trim(),
      date: (modalEventData.date || modalEventData.subtitle || '').trim(),
      description: (modalEventData.description || '').trim(),
      highlights: cleanHighlights,
      schedule: cleanSchedule,
      images: Array.isArray(modalEventData.images) ? modalEventData.images : []
    };

    const isAdd = isAddingEvent;
    const fieldId = isAdd ? `events.${eventIndex}.add` : `events.${eventIndex}.update`;
    const fieldName = isAdd ? `Add Event: ${ev.title}` : `Update Event: ${ev.title}`;
    const oldValue = isAdd ? null : (originalData.events[eventIndex] || {});

    const eventKey = `event_${eventIndex}`;
    setSubmitting(prev => ({ ...prev, [eventKey]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldId,
            field_name: fieldName,
            old_value: oldValue,
            new_value: ev,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`Event "${ev.title}" submitted for Principal approval`);
        
        if (isAdd) {
          setFormData(prev => ({
            ...prev,
            events: [...prev.events, ev]
          }));
          setOriginalData(prev => ({
            ...prev,
            events: [...prev.events, ev]
          }));
          setPendingEvents(prev => ({ ...prev, [`event_${eventIndex}_add`]: true }));
        } else {
          const updatedEvents = [...formData.events];
          updatedEvents[eventIndex] = ev;
          setFormData(prev => ({ ...prev, events: updatedEvents }));
          setOriginalData(prev => {
            const nextOrig = [...(prev.events || [])];
            nextOrig[eventIndex] = ev;
            return { ...prev, events: nextOrig };
          });
          setPendingEvents(prev => ({ ...prev, [eventKey]: true }));
        }

        handleCloseEventModal();
      } else {
        if (response.status === 409) {
          toast.error('This event already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [eventKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading Events section...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <Calendar size={22} />
        </div>
        <div>
          <h2 className="unified-title">Events & Competitions</h2>
          <p className="unified-subtitle">
            Manage signature campus milestones, sports festivals, arts showcases, and competition schedules
          </p>
        </div>
      </div>

      {/* Field 1: Kicker */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Tag / Kicker</label>
          {pendingFields.kicker && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength={80}
            className="unified-input"
            value={formData.kicker}
            onChange={(e) => handleFieldChange('kicker', e.target.value)}
            disabled={submitting.kicker || pendingFields.kicker}
            placeholder="e.g. Campus Events & Calendar"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('kicker')}
            disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
          >
            <Save size={14} /> {submitting.kicker ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field 2: Title */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Main Heading</label>
          {pendingFields.title && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength={120}
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={submitting.title || pendingFields.title}
            placeholder="e.g. Major Campus Events & Competitions"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title')}
            disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
          >
            <Save size={14} /> {submitting.title ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field 3: Description */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Overview Description</label>
          {pendingFields.description && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
          <textarea
            maxLength={600}
            className="unified-textarea"
            rows={3}
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            disabled={submitting.description || pendingFields.description}
            placeholder="From athletic tournaments to business pitches and STEM discovery, our signature events..."
          />
          <button
            type="button"
            className="unified-submit-button"
            style={{ marginTop: '2px' }}
            onClick={() => handleSubmitField('description')}
            disabled={submitting.description || pendingFields.description || formData.description === originalData.description}
          >
            <Save size={14} /> {submitting.description ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field 4: Events List */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Major Events ({formData.events.length})</label>
            <p className="unified-field-desc">
              Annual campus traditions, seasonal tournaments, performances, and student expos
            </p>
          </div>
          <button
            type="button"
            className="unified-secondary-btn"
            onClick={handleAddEvent}
          >
            <Plus size={14} /> Add Event
          </button>
        </div>

        {formData.events.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '36px 20px', color: '#64748b' }}>
            <Calendar size={32} style={{ margin: '0 auto 10px', opacity: 0.5 }} />
            <p style={{ margin: 0, fontWeight: 600 }}>No events configured</p>
            <p style={{ margin: '4px 0 0', fontSize: '13px' }}>Click "Add Event" above to create campus milestones</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '10px' }}>
            {formData.events.map((ev, idx) => {
              const isPendingDelete = pendingEvents[`event_${idx}_delete`];
              const isPendingAdd = pendingEvents[`event_${idx}_add`];
              const isPendingUpdate = pendingEvents[`event_${idx}`];
              const hasPending = isPendingDelete || isPendingAdd || isPendingUpdate;

              const eventImg = (Array.isArray(ev.images) && ev.images[0]) || '';
              const eventImgUrl = getMediaUrl(eventImg);

              let borderColor = '#e2e8f0';
              if (isPendingDelete) borderColor = '#ef4444';
              else if (hasPending) borderColor = '#f59e0b';

              return (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '14px 18px',
                    background: '#f8fafc',
                    borderRadius: '12px',
                    border: `1px solid ${borderColor}`,
                    transition: 'all 0.2s ease',
                    opacity: isPendingDelete ? 0.75 : 1
                  }}
                >
                  {/* Thumbnail */}
                  <div
                    style={{
                      width: '100px',
                      height: '75px',
                      borderRadius: '8px',
                      background: '#0f172a',
                      overflow: 'hidden',
                      flexShrink: 0,
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid #cbd5e1'
                    }}
                  >
                    {eventImgUrl ? (
                      <img
                        src={eventImgUrl}
                        alt={ev.title || `Event ${idx + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <ImageIcon size={22} color="#94a3b8" />
                    )}
                    {eventImgUrl && (
                      <button
                        type="button"
                        onClick={() => setLightboxImage(eventImgUrl)}
                        style={{
                          position: 'absolute',
                          bottom: '3px',
                          right: '3px',
                          background: 'rgba(0,0,0,0.65)',
                          color: '#fff',
                          border: 'none',
                          padding: '2px 4px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        title="Preview full photo"
                      >
                        <Eye size={10} />
                      </button>
                    )}
                  </div>

                  {/* Details */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: '800', fontSize: '15px', color: '#0f172a' }}>
                        {ev.title || `Event #${idx + 1}`}
                      </span>
                      {ev.category && (
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: '6px',
                            background: '#e0e7ff',
                            color: '#4338ca',
                            fontSize: '11px',
                            fontWeight: '600'
                          }}
                        >
                          {ev.category}
                        </span>
                      )}
                      {(ev.date || ev.subtitle) && (
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: '6px',
                            background: '#fef3c7',
                            color: '#92400e',
                            fontSize: '11px',
                            fontWeight: '700'
                          }}
                        >
                          {ev.date || ev.subtitle}
                        </span>
                      )}
                      {isPendingDelete && (
                        <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px', background: '#fef2f2', color: '#dc2626', borderColor: '#fca5a5' }}>
                          <Clock size={10} /> Pending Deletion
                        </span>
                      )}
                      {isPendingAdd && (
                        <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px' }}>
                          <Clock size={10} /> Pending Creation
                        </span>
                      )}
                      {isPendingUpdate && !isPendingDelete && (
                        <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px' }}>
                          <Clock size={10} /> Pending Review
                        </span>
                      )}
                    </div>
                    <p style={{ margin: '0 0 3px', fontSize: '13px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ev.description || 'No description provided'}
                    </p>
                    <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>
                      {(ev.highlights || []).length} highlights &bull; {(ev.schedule || []).length} schedule items &bull; {(ev.images || []).length} photos
                    </p>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                    <button
                      type="button"
                      className="unified-secondary-btn"
                      disabled={isPendingDelete}
                      onClick={() => handleOpenEventModal(idx)}
                    >
                      <Edit3 size={13} /> Edit
                    </button>
                    {!hasPending && (
                      <button
                        type="button"
                        className="unified-secondary-btn"
                        style={{ color: '#ef4444', borderColor: '#fca5a5' }}
                        disabled={submitting[`event_${idx}`]}
                        onClick={() => handleDeleteEvent(idx)}
                      >
                        <Trash2 size={13} /> Delete
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit / Add Event Modal */}
      {editingEventIndex !== null && modalEventData && (
        <div
          className="unified-modal-overlay"
          onClick={handleCloseEventModal}
        >
          <div className="unified-modal" style={{ maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="unified-modal-header">
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>
                {isAddingEvent ? 'Add New Event' : `Edit Event: ${modalEventData.title || `#${editingEventIndex + 1}`}`}
              </h3>
              <button
                type="button"
                onClick={handleCloseEventModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body">
              <div style={{ display: 'grid', gap: '16px' }}>
                <div>
                  <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>Event Title *</label>
                  <input
                    type="text"
                    value={modalEventData.title || ''}
                    onChange={(e) => setModalEventData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g. SPARTAN CUPS"
                    className="unified-input"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>Category Badge (Optional)</label>
                    <input
                      type="text"
                      value={modalEventData.category || ''}
                      onChange={(e) => setModalEventData(prev => ({ ...prev, category: e.target.value }))}
                      placeholder="e.g. Annual School Championship"
                      className="unified-input"
                    />
                    <span style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'block' }}>
                      Leave blank to auto-detect category by keyword
                    </span>
                  </div>
                  <div>
                    <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>Date / Timing</label>
                    <input
                      type="text"
                      value={modalEventData.date || ''}
                      onChange={(e) => setModalEventData(prev => ({ ...prev, date: e.target.value }))}
                      placeholder="e.g. October 15-18, 2026 or Term 1"
                      className="unified-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>Tagline / Subtitle</label>
                  <input
                    type="text"
                    value={modalEventData.subtitle || ''}
                    onChange={(e) => setModalEventData(prev => ({ ...prev, subtitle: e.target.value }))}
                    placeholder="e.g. Annual sports, arts, and leadership competitions"
                    className="unified-input"
                  />
                </div>

                <div>
                  <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>Overview Description</label>
                  <textarea
                    value={modalEventData.description || ''}
                    onChange={(e) => setModalEventData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe event highlights, student participation, and special traditions..."
                    className="unified-textarea"
                    rows={3}
                  />
                </div>

                <div>
                  <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>Signature Highlights (one per line)</label>
                  <textarea
                    value={highlightsText}
                    onChange={(e) => setHighlightsText(e.target.value)}
                    placeholder={"House leagues and leadership roles\nSkill clinics led by coaches\nChampionship night and awards"}
                    className="unified-textarea"
                    rows={3}
                  />
                  <span style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'block' }}>
                    Press Enter to separate points. Each line renders as a highlighted badge.
                  </span>
                </div>

                <div>
                  <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>Program Flow & Milestones (one per line)</label>
                  <textarea
                    value={scheduleText}
                    onChange={(e) => setScheduleText(e.target.value)}
                    placeholder={"Week 1: Team drafting and leadership workshop\nWeek 2: Qualifiers and sports clinics\nWeek 3: Creative showcase + finals"}
                    className="unified-textarea"
                    rows={3}
                  />
                  <span style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'block' }}>
                    Format with a colon (e.g. "Phase 1: Briefing") to render badge and step text.
                  </span>
                </div>

                {/* Event Photos */}
                <div>
                  <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>Event Gallery Photos</label>
                  
                  {/* Photo List */}
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                    {(Array.isArray(modalEventData.images) ? modalEventData.images : []).map((imgId, iIdx) => {
                      const u = getMediaUrl(imgId);
                      return (
                        <div key={iIdx} style={{ position: 'relative', width: '80px', height: '60px', borderRadius: '8px', overflow: 'hidden', background: '#0f172a' }}>
                          <img src={u} alt="Event" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          <button
                            type="button"
                            onClick={() => {
                              const updatedImages = modalEventData.images.filter((_, idx) => idx !== iIdx);
                              setModalEventData(prev => ({ ...prev, images: updatedImages }));
                            }}
                            style={{
                              position: 'absolute',
                              top: '2px',
                              right: '2px',
                              background: 'rgba(239, 68, 68, 0.85)',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '50%',
                              width: '18px',
                              height: '18px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '10px'
                            }}
                          >
                            <X size={10} />
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  <label className="unified-secondary-btn" style={{ cursor: imageUploading[editingEventIndex] ? 'not-allowed' : 'pointer', display: 'inline-flex' }}>
                    <Upload size={14} />
                    <span>
                      {imageUploading[editingEventIndex] 
                        ? `Uploading (${imageProgress[editingEventIndex] || 0}%)...` 
                        : 'Upload Event Photo'}
                    </span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => handleImageUpload(editingEventIndex, e)}
                      disabled={imageUploading[editingEventIndex]}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="unified-modal-footer">
              <button
                type="button"
                className="unified-secondary-btn"
                onClick={handleCloseEventModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitEvent(editingEventIndex)}
                disabled={submitting[`event_${editingEventIndex}`] || !modalEventData.title || imageUploading[editingEventIndex]}
              >
                <Save size={14} />
                {submitting[`event_${editingEventIndex}`] ? 'Submitting...' : 'Save & Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="unified-modal-overlay" onClick={() => setLightboxImage(null)}>
          <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '-12px',
                right: '-12px',
                background: '#ef4444',
                color: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: '28px',
                height: '28px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                zIndex: 10
              }}
            >
              <X size={16} />
            </button>
            <img
              src={lightboxImage}
              alt="Preview"
              style={{ maxWidth: '100%', maxHeight: '85vh', borderRadius: '12px', display: 'block' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
